import { useState, UIEvent, useMemo, useEffect, use, useRef } from 'react'

import { fetchConversationsList } from '@app/api'
import { ChatContext } from '@app/context'
import { getConversationItem, spamStatuses } from '@app/helpers'
import {
  CONVERSATIONS_LIST_SIZE,
  ListChunkPayload,
  useDependencies,
  useUser,
} from '@app/hooks'

export const useConversationList = () => {
  const [pageCursor, setPageCursor] = useState('0')
  const { dispatch, state } = use(ChatContext)
  const userState = useUser()
  const { getDependencies } = useDependencies()
  const isFetching = useRef(false)

  const conversations = useMemo(() => {
    return Array.from(Object.values(state.conversations))
      .filter((conversation) => {
        if (state.replyDetectionStatus) {
          return (
            conversation.replyDetectionStatus === state.replyDetectionStatus
          )
        }
        return true
      })
      .sort((itemA, itemB) => {
        return itemA.updateDate > itemB.updateDate ? -1 : 1
      })
  }, [state.conversations, state.replyDetectionStatus])

  const loading = useMemo(
    () => state.fetching === 'PENDING' || state.fetchingListChunk === 'PENDING',
    [state.fetching, state.fetchingListChunk]
  )

  const succeed = useMemo(() => state.fetching === 'SUCCEED', [state.fetching])

  const handleFetchListChunk = async (pageCursor: string) => {
    dispatch({ type: 'FETCH_LIST_CHUNK_START', payload: pageCursor })
    try {
      const isSpam = state.status === 'spam'
      const conversationsList = await fetchConversationsList(
        pageCursor,
        CONVERSATIONS_LIST_SIZE,
        isSpam ? spamStatuses(userState) : ['dispatched'],
        !isSpam ? state.replyDetectionStatus : undefined
      )

      const conversations = conversationsList.conversations.map((item) => {
        const previousConversation = state.conversations[item.id]
        return getConversationItem(
          item,
          state.participantIds,
          state.unreadList,
          previousConversation
        )
      })

      const { listings, accounts } = await getDependencies(conversations)

      const payload: ListChunkPayload = {
        conversations,
        listings,
        accounts,
        nextPageCursor: conversationsList.nextPageCursor,
      }

      dispatch({ type: 'FETCH_LIST_CHUNK_SUCCESS', payload })
    } catch (error) {
      dispatch({ type: 'FETCH_LIST_CHUNK_ERROR' })
    }
  }

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (state.allConversationsLoaded) return
    const { target } = event
    if (!target || loading || isFetching.current) {
      return
    }
    const { scrollTop, clientHeight, scrollHeight } = target as HTMLDivElement
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isFetching.current = true
      setPageCursor(state.nextPageCursor)
    }
  }

  useEffect(() => {
    if (state.nextPageCursor !== '0') {
      handleFetchListChunk(state.nextPageCursor).finally(() => {
        new Promise((resolve) => setTimeout(resolve, 100)).then(
          () => (isFetching.current = false)
        )
      })
    }
  }, [pageCursor])

  return {
    loading,
    succeed,
    handleScroll,
    conversations,
  }
}
